import React, { useRef, useState } from 'react';
import { createUser } from '../../api';
import { Button, InlineAlert, Modal, SelectField, TextField, useToast } from '../../ui';
import { formErrorsFrom } from '../auth/serverErrors';
import { validateRegistration } from '../auth/RegisterPage';

const FIELDS = ['username', 'password', 'firstName', 'lastName', 'email', 'role'] as const;
type Field = (typeof FIELDS)[number];
const INITIAL: Record<Field, string> = { username: '', password: '', firstName: '', lastName: '', email: '', role: 'user' };

interface AddUserDialogProps {
  open: boolean;
  onClose: () => void;
  /** called after the user was added, so the list can reload */
  onAdded: () => void;
}

/**
 * Add a colleague to the admin's own organisation (POST /users, admin only). The same rules as sign-up apply (a username
 * without spaces, a password of at least 8 characters, a valid email), plus a role: `user` (sees only the campaigns they
 * create) or `admin` (sees the organisation's campaigns and can add users). The organisation is never chosen here: the
 * server puts the new user in the admin's own.
 */
export function AddUserDialog({ open, onClose, onAdded }: AddUserDialogProps) {
  const { notify } = useToast();
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState<Partial<Record<Field, string>>>({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const savingRef = useRef(false);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) { setForm(INITIAL); setErrors({}); setFormError(''); }
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const name = e.target.name as Field;
    setForm((f) => ({ ...f, [name]: e.target.value }));
    setErrors((er) => ({ ...er, [name]: undefined }));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (savingRef.current) return;
    const rules = validateRegistration({ ...form, confirm: form.password, mobile: '' }); // the same checks as sign-up
    const next: Partial<Record<Field, string>> = {};
    (['username', 'password', 'email'] as const).forEach((k) => { if (rules[k]) next[k] = rules[k]; });
    setErrors(next);
    setFormError('');
    if (Object.keys(next).length) return;

    savingRef.current = true;
    setSaving(true);
    try {
      await createUser({ ...form, role: form.role === 'admin' ? 'admin' : 'user' });
      notify(`${form.username.trim()} was added`, { tone: 'ok' });
      onAdded();
      onClose();
    } catch (err) {
      const { fieldErrors, general } = formErrorsFrom(err, FIELDS);
      setErrors(fieldErrors);
      setFormError(general); // includes 403 when the signed-in user is not an admin
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      size="md"
      title="Add user"
      subtitle="They join your organisation."
      onClose={() => { if (!savingRef.current) onClose(); }}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button type="submit" form="add-user-form" loading={saving}>Add user</Button>
        </>
      )}
    >
      <form id="add-user-form" onSubmit={submit} noValidate className="space-y-4">
        <TextField label="Username" name="username" autoComplete="off" required value={form.username} onChange={onChange} error={errors.username} hint="No spaces. It must be unique, whatever the capital letters." />
        <TextField label="Password" name="password" type="password" autoComplete="new-password" required value={form.password} onChange={onChange} error={errors.password} hint="At least 8 characters. Share it with them safely." />
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField label="First name" name="firstName" maxLength={80} value={form.firstName} onChange={onChange} error={errors.firstName} />
          <TextField label="Last name" name="lastName" maxLength={80} value={form.lastName} onChange={onChange} error={errors.lastName} />
        </div>
        <TextField label="Email" name="email" type="email" maxLength={160} value={form.email} onChange={onChange} error={errors.email} />
        <SelectField label="Role" name="role" value={form.role} onChange={onChange} error={errors.role} hint={form.role === 'admin' ? 'Sees every campaign in the organisation and can add users.' : 'Sees only the campaigns they create.'}>
          <option value="user">User</option>
          <option value="admin">Admin</option>
        </SelectField>
        {formError && <InlineAlert>{formError}</InlineAlert>}
      </form>
    </Modal>
  );
}
